import { useEffect, useId, useLayoutEffect, useRef, useState } from "react"
import "./ImageSelect.css"

// Ask Unsplash for a small square instead of the full-size picture
export const thumb = (url, size = 96) => {
  if (!url) return ""
  if (!/images\.unsplash\.com/.test(url)) return url
  const u = new URL(url)
  u.searchParams.set("w", size)
  u.searchParams.set("h", size)
  u.searchParams.set("fit", "crop")
  u.searchParams.set("auto", "format")
  u.searchParams.delete("crop")
  return u.toString()
}

/**
 * A dropdown whose options each show a small picture.
 * options: [{ value, label, image }]. Keyboard: ↑ ↓ Home End, Enter/Space to pick,
 * Esc to close, or type a letter to jump.
 * compact: small version for tight spots (e.g. the homepage category cards); the label is
 * then only read by screen readers.
 */
export default function ImageSelect({ id, label, value, options, onChange, placeholder = "Choose…", compact = false }) {
  const autoId = useId()
  const baseId = id || autoId
  const listId = `${baseId}-list`
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [alignRight, setAlignRight] = useState(false)
  const rootRef = useRef(null)
  const buttonRef = useRef(null)
  const listRef = useRef(null)

  const selected = options.find((o) => o.value === value)

  /* Close when clicking anywhere else */
  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [open])

  /* If the menu would run off the right edge of the screen, open it leftwards instead */
  useLayoutEffect(() => {
    if (!open) return setAlignRight(false)
    const r = listRef.current?.getBoundingClientRect()
    if (r && r.right > window.innerWidth - 8) setAlignRight(true)
  }, [open])

  /* Keep the highlighted option in view */
  useEffect(() => {
    if (!open || active < 0) return
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" })
  }, [open, active])

  const openList = () => {
    setActive(Math.max(0, options.findIndex((o) => o.value === value)))
    setOpen(true)
    requestAnimationFrame(() => listRef.current?.focus())
  }

  const choose = (opt) => {
    onChange(opt.value)
    setOpen(false)
    buttonRef.current?.focus()
  }

  const onButtonKey = (e) => {
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
      e.preventDefault()
      openList()
    }
  }

  const onListKey = (e) => {
    const last = options.length - 1
    if (e.key === "ArrowDown") setActive((a) => Math.min(last, a + 1))
    else if (e.key === "ArrowUp") setActive((a) => Math.max(0, a - 1))
    else if (e.key === "Home") setActive(0)
    else if (e.key === "End") setActive(last)
    else if (e.key === "Enter" || e.key === " ") {
      if (options[active]) choose(options[active])
    } else if (e.key === "Escape" || e.key === "Tab") {
      setOpen(false)
      if (e.key === "Escape") buttonRef.current?.focus()
      return
    } else if (e.key.length === 1 && /\S/.test(e.key)) {
      // Type-ahead: jump to the next option starting with that letter
      const k = e.key.toLowerCase()
      const order = [...options.slice(active + 1), ...options.slice(0, active + 1)]
      const hit = order.find((o) => o.label.toLowerCase().startsWith(k))
      if (hit) setActive(options.indexOf(hit))
      else return
    } else return
    e.preventDefault()
  }

  return (
    <div className={`image-select ${compact ? "is-compact mb-2" : "mb-3"}`} ref={rootRef}>
      <span className={compact ? "visually-hidden" : "image-select-label"} id={`${baseId}-label`}>
        {label}
      </span>

      <button
        ref={buttonRef}
        id={baseId}
        type="button"
        className={`image-select-button ${open ? "is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-labelledby={`${baseId}-label ${baseId}`}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onButtonKey}
      >
        {selected?.image ? (
          <img className="image-select-thumb" src={thumb(selected.image)} alt="" />
        ) : (
          <span className="image-select-thumb is-empty" aria-hidden="true" />
        )}
        <span className={`image-select-value ${selected ? "" : "text-muted"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg className="image-select-chevron" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          className={`image-select-list ${alignRight ? "is-right" : ""}`}
          aria-labelledby={`${baseId}-label`}
          aria-activedescendant={active >= 0 ? `${baseId}-opt-${active}` : undefined}
          onKeyDown={onListKey}
        >
          {options.map((o, i) => (
            <li
              key={o.value}
              id={`${baseId}-opt-${i}`}
              data-index={i}
              role="option"
              aria-selected={o.value === value}
              className={`image-select-option ${i === active ? "is-active" : ""}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(o)}
            >
              {o.image ? (
                <img className="image-select-thumb" src={thumb(o.image)} alt="" loading="lazy" />
              ) : (
                <span className="image-select-thumb is-empty" aria-hidden="true" />
              )}
              <span className="image-select-option-label">{o.label}</span>
              {o.value === value && (
                <svg className="image-select-check" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
